import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type EventAdmin, type Item, type ParticipantSelf } from "@/lib/api";
import { recentEventsStorage } from "@/lib/storage";

function adminPath(shortcut: string, key: string): string {
  return `/api/v1/events/${shortcut}/admin/${key}`;
}

export function useAdminEvent(shortcut: string, adminKey: string) {
  return useQuery({
    queryKey: ["event-admin", shortcut, adminKey],
    queryFn: () => api.get<EventAdmin>(adminPath(shortcut, adminKey)),
    enabled: Boolean(shortcut && adminKey),
  });
}

function useInvalidate(shortcut: string, adminKey: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["event-admin", shortcut, adminKey] });
}

export function useUpdateRecentEntry() {
  return (event: EventAdmin) => {
    recentEventsStorage.upsert({
      shortcut: event.shortcut,
      key: event.adminKey,
      role: "admin",
      name: event.name,
      eventAt: event.eventAt,
    });
  };
}

// ---------------------------------------------------------------------------
// Teilnehmer-Mutations
// ---------------------------------------------------------------------------

export function useAddParticipant(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: (payload: { name: string; persons: number }) =>
      api.post<ParticipantSelf>(`${adminPath(shortcut, adminKey)}/participants`, payload),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateParticipant(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: number;
      name?: string;
      participates?: boolean;
      persons?: number;
    }) =>
      api.put<ParticipantSelf>(
        `${adminPath(shortcut, adminKey)}/participants/${id}`,
        payload,
      ),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteParticipant(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: (participantId: number) =>
      api.delete(`${adminPath(shortcut, adminKey)}/participants/${participantId}`),
    onSuccess: () => invalidate(),
  });
}

// ---------------------------------------------------------------------------
// Item-Mutations
// ---------------------------------------------------------------------------

export function useAddItem(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: (payload: { name: string; comment?: string; forAllUsers?: boolean }) =>
      api.post<Item>(`${adminPath(shortcut, adminKey)}/items`, payload),
    onSuccess: () => invalidate(),
  });
}

export type ItemUpdatePayload = {
  id: number;
  name?: string;
  comment?: string | null;
  forAllUsers?: boolean;
  /** `null` = Zuweisung aufheben; Feld weglassen = nicht antasten. */
  participantId?: number | null;
};

export function useUpdateItem(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: ({ id, ...payload }: ItemUpdatePayload) =>
      api.put<Item>(`${adminPath(shortcut, adminKey)}/items/${id}`, payload),
    onSuccess: () => invalidate(),
  });
}

export function useDuplicateItem(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: (id: number) =>
      api.post<Item>(`${adminPath(shortcut, adminKey)}/items/${id}/duplicate`),
    onSuccess: () => invalidate(),
  });
}

export function useSetItemPosition(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: ({ id, position }: { id: number; position: number }) =>
      api.patch<Item>(`${adminPath(shortcut, adminKey)}/items/${id}/position`, { position }),
    onSuccess: () => invalidate(),
  });
}

/** Swap zweier Item-Positionen als eine logische Mutation mit
 * optimistischem Update: wir tauschen die `position`-Werte lokal sofort
 * und feuern beide PATCHes parallel. Das halbiert die UI-Latenz und
 * verhindert Zwischenzustaende, in denen der User kurzzeitig dieselbe
 * Position doppelt sehen wuerde. */
export function useSwapItemPositions(shortcut: string, adminKey: string) {
  const qc = useQueryClient();
  const key = ["event-admin", shortcut, adminKey] as const;
  return useMutation({
    mutationFn: async ({ a, b }: { a: { id: number; position: number }; b: { id: number; position: number } }) => {
      await Promise.all([
        api.patch<Item>(`${adminPath(shortcut, adminKey)}/items/${a.id}/position`, {
          position: b.position,
        }),
        api.patch<Item>(`${adminPath(shortcut, adminKey)}/items/${b.id}/position`, {
          position: a.position,
        }),
      ]);
    },
    onMutate: async ({ a, b }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<EventAdmin>(key);
      if (prev) {
        qc.setQueryData<EventAdmin>(key, {
          ...prev,
          items: prev.items.map((it) =>
            it.id === a.id
              ? { ...it, position: b.position }
              : it.id === b.id
                ? { ...it, position: a.position }
                : it,
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

/** Verschiebt mehrere Items gleichzeitig auf neue Positionen. Wird vom
 * Drag-&-Drop-Flow aufgerufen, wenn ein Item von Index A an Index B gezogen
 * wird — dabei verschieben sich bis zu N Nachbarn. Optimistic Update,
 * parallele PATCHes, rollback bei Fehler. */
export function useReorderItems(shortcut: string, adminKey: string) {
  const qc = useQueryClient();
  const key = ["event-admin", shortcut, adminKey] as const;
  return useMutation({
    mutationFn: async (updates: Array<{ id: number; position: number }>) => {
      await Promise.all(
        updates.map((u) =>
          api.patch<Item>(`${adminPath(shortcut, adminKey)}/items/${u.id}/position`, {
            position: u.position,
          }),
        ),
      );
    },
    onMutate: async (updates) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<EventAdmin>(key);
      if (prev) {
        const byId = new Map(updates.map((u) => [u.id, u.position] as const));
        qc.setQueryData<EventAdmin>(key, {
          ...prev,
          items: prev.items.map((it) =>
            byId.has(it.id) ? { ...it, position: byId.get(it.id)! } : it,
          ),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}

export function useDeleteItem(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: (id: number) => api.delete(`${adminPath(shortcut, adminKey)}/items/${id}`),
    onSuccess: () => invalidate(),
  });
}

export function useRestoreItem(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: (id: number) =>
      api.post<Item>(`${adminPath(shortcut, adminKey)}/items/${id}/restore`),
    onSuccess: () => invalidate(),
  });
}

export function usePurgeItem(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: (id: number) =>
      api.delete(`${adminPath(shortcut, adminKey)}/items/${id}/purge`),
    onSuccess: () => invalidate(),
  });
}

// ---------------------------------------------------------------------------
// Event-Update / Cancel / Uncancel
// ---------------------------------------------------------------------------

export type EventUpdatePayload = {
  name?: string;
  eventAt?: string;
  timezone?: string;
  description?: string | null;
  location?: string | null;
  deadline?: string | null;
  creatorName?: string | null;
  expandable?: boolean;
  anonymous?: boolean;
};

export function useUpdateEvent(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: (payload: EventUpdatePayload) =>
      api.put<EventAdmin>(adminPath(shortcut, adminKey), payload),
    onSuccess: () => invalidate(),
  });
}

export function useCancelEvent(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: (payload: { cancelReason?: string }) =>
      api.post<EventAdmin>(`${adminPath(shortcut, adminKey)}/cancel`, payload),
    onSuccess: () => invalidate(),
  });
}

export function useUncancelEvent(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: () => api.post<EventAdmin>(`${adminPath(shortcut, adminKey)}/uncancel`),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteEvent(shortcut: string, adminKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(adminPath(shortcut, adminKey)),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ["event-admin", shortcut, adminKey] });
    },
  });
}

export type EventStatsDay = { date: string; views: number; interactions: number };

export function useEventStats(
  shortcut: string,
  adminKey: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["event-stats", shortcut, adminKey],
    queryFn: () =>
      api.get<{ days: EventStatsDay[] }>(`${adminPath(shortcut, adminKey)}/stats`),
    enabled: enabled && Boolean(shortcut && adminKey),
    staleTime: 60_000,
  });
}

export function useStartUpgradeCheckout(shortcut: string, adminKey: string) {
  return useMutation({
    mutationFn: () =>
      api.post<{ checkoutUrl: string }>(`${adminPath(shortcut, adminKey)}/upgrade`),
  });
}

export function useUpdateEmail(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: (creatorEmail: string | null) =>
      api.put<void>(`${adminPath(shortcut, adminKey)}/email`, { creatorEmail }),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateNotifications(shortcut: string, adminKey: string) {
  const invalidate = useInvalidate(shortcut, adminKey);
  return useMutation({
    mutationFn: (payload: { notifyOnChanges: boolean; notifySummary: boolean }) =>
      api.put<{ notifyOnChanges: boolean; notifySummary: boolean }>(
        `${adminPath(shortcut, adminKey)}/notifications`,
        payload,
      ),
    onSuccess: () => invalidate(),
  });
}
